// utils/elementFormat/url.js
//
// How a `url` element NORMALISES and which links it REFUSES (keepr-web
// docs/plans/data-types-second-pass.md §3.2, decision D5 as the owner ruled it
// on 2026-09-30, with the curated scheme list below): one rule for every
// write path — the form, POST/PUT /api/items, bulk, batch ingest, multi add,
// quick add, a public intake form and the automation actions.
//
// PURE AND DEPENDENCY-FREE ON PURPOSE, and vendored byte-identical into
// keepr-web at `src/utils/elementFormat/url.js` so the form's blur (which
// shows the normalised link before the save) and the API's 400 `invalid_url`
// can never disagree about which links exist. MIRRORED in keepr-web — change
// them together, with the shared vectors in `vectors.json` beside this file.
// Nothing here may require() anything, and nothing here may know about Mongo,
// Boom or the request.
//
// THE RULE:
//
//   normalise  Trim. A value with no scheme that reads as a HOST
//              ("example.com/manual", "www.example.com", "10.0.0.12:8080")
//              gets "https://" in front, because that is what every browser
//              and every person means by it. Nothing else is changed: no
//              lower-casing (paths are case-sensitive), no trailing slash, no
//              percent-encoding. What the person typed is what is stored.
//   refuse     A scheme that is not on SCHEMES (javascript:, data:, file:,
//              ftp:, ms-msdt: — DENIED_SCHEMES names the ones that must never
//              be added), any whitespace or control character anywhere (a browser
//              silently drops a tab inside "java\tscript:", so it is refused
//              rather than read), an http(s) link with no host, any other
//              allowed scheme with nothing after it, anything that is neither a link
//              nor a host, and anything longer than MAX_LENGTH once
//              normalised.
//
// The ECHO rule is the caller's, not this module's: a value identical to the
// one already stored always saves (utils/typedElements.js), so no link that
// predates the rule bricks an item. The web's `urlHref` stays the render-time
// backstop for those.

'use strict';

// The platform's URL ceiling (utils/elementFormat/text.js holds the same
// number in its CEILINGS table; test/elementFormatText.test.js pins that the
// two agree). 2,048 is the longest URL every browser in use still opens.
const MAX_LENGTH = 2048;

// The schemes a stored link may carry — THE list; trim or extend it here and
// both repos follow (keepr-web vendors this file). The two that navigate; the
// ones the value action hands to a mail client, a dialer and a messages app;
// a map point (geo:); and the calling, music and meeting apps people keep
// links to (FaceTime, Spotify, Zoom, Teams, Slack). Every one opens an app
// the person chose to install; none of them runs code in the page.
const SCHEMES = ['http', 'https', 'mailto', 'tel', 'sms', 'geo', 'facetime', 'spotify', 'zoommtg', 'msteams', 'slack'];
const DEFAULT_SCHEME = 'https';

// Schemes that must NEVER join SCHEMES, checked first so the refusal holds
// even if one is added there by mistake (a test pins that the two lists never
// meet): script (javascript:, vbscript:, and data:, which can carry a whole
// page), local files (file:, and smb:, which can leak Windows credentials to
// whoever runs the share), and every ms-* scheme — the Office and Windows
// handlers, one of which (ms-msdt:) was the Follina exploit.
const DENIED_SCHEMES = ['javascript', 'data', 'vbscript', 'file', 'smb'];
const DENIED_PREFIXES = ['ms-'];

/** True when a (lower-cased) scheme is one that must never be stored. */
function isDeniedScheme(scheme) {
	return DENIED_SCHEMES.includes(scheme) || DENIED_PREFIXES.some(prefix => scheme.startsWith(prefix));
}

/**
 * The allowed schemes as a person reads them, for the refusal message both
 * repos show: "https://, http://, mailto:, tel:, … or slack:". Built from
 * SCHEMES, so trimming the list trims the sentence.
 */
function allowedSchemesText() {
	const spelled = ['https://', 'http://', ...SCHEMES.filter(s => s !== 'http' && s !== 'https').map(s => `${s}:`)];
	return `${spelled.slice(0, -1).join(', ')} or ${spelled[spelled.length - 1]}`;
}

// Whitespace of any kind (Unicode included), the C0 and C1 control ranges and
// DEL. A URL never needs one — a space is %20 — and a browser strips some of
// them before it reads the scheme, which is how "java\tscript:" becomes
// "javascript:". Refusing them is what makes the scheme test below honest.
const REFUSED_CHAR = /[\s\u0000-\u001f\u007f-\u009f]/; // eslint-disable-line no-control-regex

// A scheme is a letter, then letters, digits, '+', '-' or '.', then ':'.
const SCHEME_RE = /^([A-Za-z][A-Za-z0-9+.-]*):/;

// A HOST: dot-separated labels of letters, digits and hyphens (Unicode
// letters admitted, as the email rule admits them), a hyphen never on the
// edge of a label, a final label of two or more characters that starts with
// a letter — or a dotted IPv4 address — then an optional port, then the end
// of the value or the start of a path, query or fragment. Labels are bounded
// at 63 characters (the DNS limit), which also bounds the regex's work.
const L = 'A-Za-z0-9\\u00a1-\\uffff';
const LABEL = `[${L}](?:[${L}-]{0,61}[${L}])?`;
const TLD = `[A-Za-z\\u00a1-\\uffff](?:[${L}-]{0,61}[${L}])`;
const IPV4 = '(?:\\d{1,3}\\.){3}\\d{1,3}';
const HOST_RE = new RegExp(`^(?:(?:${LABEL}\\.)+${TLD}|${IPV4})(?::\\d{1,5})?(?:[/?#]|$)`);

// After "http:" / "https:", two slashes and a non-empty authority.
const AUTHORITY_RE = /^\/\/[^/?#]+/;

const toStr = v => (v === null || v === undefined ? '' : String(v));

/**
 * countChars(text) -> number
 *
 * Characters as a person counts them: a code point, so an emoji is one
 * character and not the two UTF-16 units JavaScript's `.length` reports.
 */
function countChars(text) {
	const s = toStr(text);
	let n = s.length;
	for (let i = 0; i < s.length - 1; i++) {
		const c = s.charCodeAt(i);
		if (c >= 0xd800 && c <= 0xdbff) {
			const d = s.charCodeAt(i + 1);
			if (d >= 0xdc00 && d <= 0xdfff) { n--; i++; }
		}
	}
	return n;
}

/** True when the value, with no scheme of its own, reads as a host. */
function readsAsHost(text) {
	return HOST_RE.test(text);
}

/**
 * schemeOf(raw) -> 'https' | 'mailto' | ... | null
 *
 * The scheme a value carries, lower-cased, or null when it carries none (a
 * bare host counts as none — it is about to become https). Read after the
 * trim and before anything else, so the answer is the one the rule judges.
 */
function schemeOf(raw) {
	const text = toStr(raw).trim();
	if (!text || readsAsHost(text)) return null;
	const match = SCHEME_RE.exec(text);
	return match ? match[1].toLowerCase() : null;
}

/**
 * normalizeUrl(raw) -> { value, valid, reason }
 *
 *   value   the link as it will be STORED (trimmed, "https://" in front of a
 *           bare host), and '' whenever `valid` is false, so a caller that
 *           stores the result without checking cannot persist a refused link.
 *   valid   the link is one the platform stores.
 *   reason  why it was refused, or null: 'empty' | 'type' | 'whitespace' |
 *           'too_long' | 'scheme' | 'no_host' | 'no_target' | 'not_a_url'.
 *           The callers hang their own sentences on these tokens (the API's
 *           400 message, the web field's message), so the two say the same
 *           thing.
 *
 * A blank input is not valid HERE ('empty'): emptiness is the caller's
 * question, and an untouched field is not a malformed link.
 */
function normalizeUrl(raw) {
	if (raw !== null && raw !== undefined && typeof raw !== 'string') return { value: '', valid: false, reason: 'type' };
	const text = toStr(raw).trim();
	if (!text) return { value: '', valid: false, reason: 'empty' };
	// Length first: nothing below should spend time on a value that is
	// refused whatever it spells.
	if (countChars(text) > MAX_LENGTH) return { value: '', valid: false, reason: 'too_long' };
	if (REFUSED_CHAR.test(text)) return { value: '', valid: false, reason: 'whitespace' };

	if (readsAsHost(text)) {
		const value = `${DEFAULT_SCHEME}://${text}`;
		if (countChars(value) > MAX_LENGTH) return { value: '', valid: false, reason: 'too_long' };
		return { value, valid: true, reason: null };
	}

	const match = SCHEME_RE.exec(text);
	if (!match) return { value: '', valid: false, reason: 'not_a_url' };
	const scheme = match[1].toLowerCase();
	if (isDeniedScheme(scheme) || !SCHEMES.includes(scheme)) return { value: '', valid: false, reason: 'scheme' };
	const rest = text.slice(match[0].length);
	if (scheme === 'http' || scheme === 'https') {
		if (!AUTHORITY_RE.test(rest)) return { value: '', valid: false, reason: 'no_host' };
	} else if (!rest) {
		return { value: '', valid: false, reason: 'no_target' };
	}
	return { value: text, valid: true, reason: null };
}

/** True when `raw` is a link this platform will store. */
function isValidUrl(raw) {
	return normalizeUrl(raw).valid;
}

module.exports = {
	MAX_LENGTH,
	SCHEMES,
	DEFAULT_SCHEME,
	DENIED_SCHEMES,
	DENIED_PREFIXES,
	isDeniedScheme,
	allowedSchemesText,
	countChars,
	readsAsHost,
	schemeOf,
	normalizeUrl,
	isValidUrl,
};
